/** Maps keyboard events to specific task list actions like indentation, movement, and navigation. */
import { getKeysAtIndex } from '@/features/tasks/utils/structure/taskHierarchy';
import {
  handleIndent,
  handleOutdent,
} from '@/features/tasks/utils/interactions/listIndentActions';

/** @deprecated Tab no longer binds here; kept for Ctrl+]/Ctrl+[ wrappers. */
const handleTabAction = (state, event) => {
  if (state.disableStructuralNav) return;
  event.preventDefault();
  if (state.selectedIndex > -1) {
    if (event.shiftKey) {
      handleOutdent(state);
    } else {
      handleIndent(state);
    }
  }
};

const handleIndentKey = (state, event) => {
  handleTabAction(state, {
    preventDefault: () => event.preventDefault(),
    shiftKey: false,
  });
};

const handleOutdentKey = (state, event) => {
  handleTabAction(state, {
    preventDefault: () => event.preventDefault(),
    shiftKey: true,
  });
};

const handleStartFocusAction = (state, event) => {
  if (state.selectedIndex < 0) return;
  const task = state.filteredTasks[state.selectedIndex];
  if (!task) return;
  event.preventDefault();
  if (state.activeFocusTaskId === task.id) {
    state.stopFocus?.();
  } else {
    state.handleStartFocus?.(task.id, state.userId);
  }
};

const handleF2Action = (state, event) => {
  event.preventDefault();
  if (state.selectedIndex > -1) {
    const {
      selectedIndex,
      setSelectedIndex,
      onStartEditing,
      filteredTasks,
      previousSelectedIndexRef,
    } = state;
    const selectedTask = filteredTasks[selectedIndex];
    previousSelectedIndexRef.current = selectedIndex;
    setSelectedIndex(-1);
    onStartEditing(selectedTask.id);
  }
};

const handleShowNewTaskInputAction = (state, event, insertAbove, key) => {
  if (state.isNewTaskInputActive || state.editingTaskId) return;
  if (!state.deselectAllTasks || !state.previousSelectedIndexRef) return;

  event.preventDefault();
  state.previousSelectedIndexRef.current = state.selectedIndex;
  state.deselectAllTasks();
  const position = getSafeInsertPosition(state, insertAbove);
  state.onShowNewTaskInput(position, key);
};

const handleToggleAction = (state, _event) => {
  if (state.selectedIndex > -1) {
    const task = state.filteredTasks[state.selectedIndex];
    state.handleToggleComplete(task, !task.completed);
  }
};

const handleFollowUpAction = (state, _event) => {
  if (state.selectedIndex > -1 && state.triggerFollowUp && state.userId) {
    const task = state.filteredTasks[state.selectedIndex];
    state.triggerFollowUp(task.id, state.userId);
  }
};

const handleDeleteAction = (state, _event) => {
  const taskToDelete =
    state.selectedIndex > -1 ? state.filteredTasks[state.selectedIndex] : null;
  if (
    taskToDelete &&
    window.confirm(`Taak "${taskToDelete.title}" verwijderen?`)
  ) {
    state.handleDeleteTask(taskToDelete.id);
    const newIndex =
      state.selectedIndex >= state.filteredTasks.length - 1
        ? state.selectedIndex - 1
        : state.selectedIndex;
    state.setSelectedIndex(newIndex);
  }
};

const handleEnterAction = (state, event) => {
  if (state.isNewTaskInputActive) return;
  if (state.selectedIndex > -1) {
    event.preventDefault();
    if (state.disableRowNavigate) return;
    const taskId = state.filteredTasks[state.selectedIndex].id;
    const pathname = state.pathname ?? window.location.pathname;
    if (pathname) {
      state.navigate(`${pathname}?task=${taskId}`);
    } else {
      state.navigate(`/app/task/${taskId}`);
    }
  }
};

const handleEscapeAction = (state, _event) => {
  if (state.isNewTaskInputActive) return;
  if (state.selectedIndex > -1) {
    state.deselectAllTasks();
  }
};

const handleAddSubtaskAction = (state, event) => {
  if (state.isNewTaskInputActive || state.editingTaskId || state.addingSubtaskTo) {
    return;
  }
  if (state.selectedIndex < 0 || !state.handleStartAddingSubtask) return;

  const task = state.filteredTasks[state.selectedIndex];
  if (!task) return;

  event.preventDefault();
  state.handleStartAddingSubtask(task.id);
};

export const keyActions = {
  F2: handleF2Action,
  a: (state, event) => handleShowNewTaskInputAction(state, event, true, 'a'),
  b: (state, event) => handleShowNewTaskInputAction(state, event, false, 'b'),
  s: handleAddSubtaskAction,
  ' ': handleToggleAction,
  'Ctrl+Space': handleFollowUpAction,
  'Ctrl+]': handleIndentKey,
  'Ctrl+[': handleOutdentKey,
  'Ctrl+Enter': handleStartFocusAction,
  Delete: handleDeleteAction,
  Enter: handleEnterAction,
  Escape: handleEscapeAction,
};

const getSafeInsertPosition = (state, insertAbove) => {
  if (state.selectedIndex === -1)
    return insertAbove ? 0 : state.filteredTasks.length;
  const targetIndex = insertAbove
    ? Math.max(0, state.selectedIndex)
    : state.selectedIndex + 1;
  return getKeysAtIndex(targetIndex, state.filteredTasks);
};

export { handleReorder } from './listReorderHelpers';
